import React, { useContext, useEffect, useMemo, useState } from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import { Button, Form, InputGroup, Pagination, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";
import ExternalRecipientBadge, { isExternalRecipient } from "../../components/forms/ExternalRecipientBadge";
// Team Forms reuses the Ticket Dashboard page frame + header card (every rule
// there hangs off `.tech-ds`, which only the team-only view opts into).
import "../technicians/TechnicianDesignSystem.css";
import "./FormResponsesPage.css";

// Dynamic Forms visibility audit, §7: the canonical Forms Workspace ->
// Review & Reports -> Responses table -- distinct from My Results (own
// submissions only), Review Queue (pending decisions only), Execution
// Reports (UAT/TESTING-profiled forms only), and Forms Assigned By Me
// (assignment-level, not submission-level). Reads GET /reports/form-
// submissions (services/reportQueryService.js#queryFormSubmissions), which
// queries the existing form_submissions table directly -- no duplicated
// storage, no new backend concept. Server-side scope (SELF/TEAM/PROJECT/
// ALL) is enforced by controllers/reportController.js's resolveReportScope,
// never re-filtered here.
const STATUS_OPTIONS = ["DRAFT", "SUBMITTED", "REOPENED", "CANCELLED"];
const REVIEW_STATUS_OPTIONS = ["NOT_REQUIRED", "PENDING_REVIEW", "PENDING_APPROVAL", "APPROVED", "REJECTED", "CHANGES_REQUESTED"];
const CHANNEL_OPTIONS = ["PORTAL", "PUBLIC_LINK"];

const STATUS_VARIANT = { DRAFT: "secondary", SUBMITTED: "success", REOPENED: "warning", CANCELLED: "danger" };
const REVIEW_VARIANT = { NOT_REQUIRED: "secondary", PENDING_REVIEW: "warning", PENDING_APPROVAL: "warning", APPROVED: "success", REJECTED: "danger", CHANGES_REQUESTED: "warning" };

function useRolePrefix() {
  const path = window.location.pathname;
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : "admin";
}

const norm = (v) => String(v ?? "").trim().toLowerCase();

// teamOnly (Manager dashboard's "View Team Forms"): purely a client-side
// narrowing of the SAME GET /reports/form-submissions result -- keeps only
// responses submitted by / assigned to the logged-in manager's team members
// (GET /manager/team-users minus the manager). The request, the server-side
// scope and every other consumer of this page are unchanged.
export default function FormResponsesPage({ embedded = false, teamOnly = false } = {}) {
  const navigate = useNavigate();
  const rolePrefix = useRolePrefix();
  const { user } = useContext(AuthContext) || {};
  const [team, setTeam] = useState({ ids: new Set(), names: new Set(), loading: teamOnly });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formTypeFilter, setFormTypeFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewStatusFilter, setReviewStatusFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [search, setSearch] = useState("");
  const [formTypes, setFormTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const fetchRows = async () => {
    try {
      setLoading(true);
      if (teamOnly) {
        // Team-only view filters client-side, so pull every page the server
        // allows for the current filters (capped) and paginate locally.
        const all = [];
        for (let pg = 1; pg <= 20; pg += 1) {
          const r = await api.get("/reports/form-submissions", {
            params: {
              form_type_id: formTypeFilter || undefined,
              project_id: projectFilter || undefined,
              status: statusFilter || undefined,
              review_status: reviewStatusFilter || undefined,
              channel: channelFilter || undefined,
              search: search || undefined,
              page: pg,
              pageSize: 100,
            },
          });
          const chunk = Array.isArray(r.data?.data) ? r.data.data : [];
          all.push(...chunk);
          if (!chunk.length || all.length >= (r.data?.meta?.total || 0)) break;
        }
        setRows(all);
        setTotal(all.length);
        return;
      }
      const res = await api.get("/reports/form-submissions", {
        params: {
          form_type_id: formTypeFilter || undefined,
          project_id: projectFilter || undefined,
          status: statusFilter || undefined,
          review_status: reviewStatusFilter || undefined,
          channel: channelFilter || undefined,
          search: search || undefined,
          page: currentPage,
          pageSize,
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error("Fetch form responses error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch form responses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTypeFilter, projectFilter, statusFilter, reviewStatusFilter, channelFilter, search, teamOnly ? 1 : currentPage]);

  useEffect(() => {
    if (!teamOnly) return;
    api.get("/manager/team-users")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const selfId = user?.id !== undefined && user?.id !== null ? String(user.id) : null;
        const selfName = norm(user?.name);
        const ids = new Set();
        const names = new Set();
        list.forEach((u) => {
          if (selfId && String(u.id) === selfId) return;
          const nm = norm(u.name || u.full_name || u.username);
          if (selfName && nm === selfName) return;
          if (u.id !== undefined && u.id !== null) ids.add(String(u.id));
          if (nm) names.add(nm);
        });
        setTeam({ ids, names, loading: false });
      })
      .catch(() => setTeam({ ids: new Set(), names: new Set(), loading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamOnly]);

  const teamRows = useMemo(() => {
    if (!teamOnly) return rows;
    const hit = (id, name) => (id !== undefined && id !== null && team.ids.has(String(id))) || (name && team.names.has(norm(name)));
    return rows.filter((r) => hit(r.submitted_by, r.submitted_by_name) || hit(r.assigned_to, r.assigned_to_name)
      || hit(r.submitted_by_user_id, null) || hit(r.assigned_to_user_id, null));
  }, [rows, teamOnly, team]);
  const viewRows = teamOnly ? teamRows.slice((currentPage - 1) * pageSize, currentPage * pageSize) : rows;
  const viewTotal = teamOnly ? teamRows.length : total;

  useEffect(() => {
    Promise.all([
      api.get("/form-types", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/projects", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
    ]).then(([typeRes, projectRes]) => {
      setFormTypes(Array.isArray(typeRes?.data?.data) ? typeRes.data.data : []);
      setProjects(Array.isArray(projectRes?.data?.data) ? projectRes.data.data : []);
    });
  }, []);

  const totalPages = Math.max(Math.ceil(viewTotal / pageSize), 1);
  const isLoading = loading || (teamOnly && team.loading);

  // Cell renderers shared by the desktop table and the mobile cards, so both
  // layouts always show exactly the same data.
  const dash = <span className="text-muted fr-muted">—</span>;
  const renderSubmission = (row) => (
    <>#{row.submission_id}{row.submission_revision > 1 ? ` (rev ${row.submission_revision})` : ""}</>
  );
  const renderProject = (row) => (
    row.project_name || row.project_module_name
      ? <>{row.project_name || "—"}{row.project_module_name ? ` / ${row.project_module_name}` : ""}</>
      : dash
  );
  const renderTask = (row) => row.related_task_title || dash;
  const renderAssigned = (row) => (
    <>
      {row.assigned_to_name || dash}
      <ExternalRecipientBadge external={isExternalRecipient(row)} />
      {isExternalRecipient(row) && row.assigned_to_email && row.assigned_to_email !== row.assigned_to_name && (
        <div className="text-muted small fr-sub">{row.assigned_to_email}</div>
      )}
    </>
  );
  const renderSubmittedBy = (row) => row.submitted_by_name || dash;
  const renderChannel = (row) => (
    <Badge bg="light" text="dark" className="fr-badge">{row.submission_channel === "PUBLIC_LINK" ? "Public Link" : "Portal"}</Badge>
  );
  const renderStatus = (row) => (
    <Badge bg={STATUS_VARIANT[row.status] || "secondary"} className="fr-badge">{row.status}</Badge>
  );
  const renderReview = (row) => (
    row.review_status && row.review_status !== "NOT_REQUIRED"
      ? <Badge bg={REVIEW_VARIANT[row.review_status] || "secondary"} className="fr-badge">{row.review_status.replace(/_/g, " ")}</Badge>
      : <span className="text-muted small fr-muted">N/A</span>
  );
  const renderScore = (row) => (
    <>
      {row.percentage != null ? <strong>{row.percentage}%</strong> : dash}
      {row.grade && <span className="text-muted"> ({row.grade})</span>}
    </>
  );
  const renderViewButton = (row) => (
    <Button
      size="sm"
      variant="outline-primary"
      className="forms-view-response-btn fr-view-btn"
      onClick={() => navigate(`/${rolePrefix}/form-submissions/${row.submission_id}`)}
    >
      <i className="fa-solid fa-eye" aria-hidden="true"></i>
      View Response
    </Button>
  );

  const refreshButton = (
    <Button className="unit-btn unit-btn-light" onClick={fetchRows}>
      <i className="fa-solid fa-rotate" aria-hidden="true"></i>
      Refresh
    </Button>
  );

  return (
    <div className={`unit-page formresponses-theme ${embedded ? "fa-embedded" : ""} ${teamOnly ? "tech-ds fr-team" : ""}`.trim()}>

      {teamOnly ? (
        // Same white header card as the Ticket Dashboard (/technician/tasks).
        <div className="unit-header tech-page-header">
          <div>
            <div className="unit-page-label tech-section-label">Form Management</div>
            <h1 className="tech-page-title">Team Forms</h1>
            <p className="tech-page-subtitle">Form responses submitted by your team members.</p>
          </div>
          <div className="unit-header-actions tech-header-actions">{refreshButton}</div>
        </div>
      ) : (
        <div className="unit-header">
          {!embedded && (
            <div>
              <div className="unit-page-label">Forms</div>
              <h2>Responses</h2>
              <p>Every form submission you're authorized to see, across every form type, with its current status.</p>
            </div>
          )}
          <div className="unit-header-actions">{refreshButton}</div>
        </div>
      )}

      <div className="fr-card">
        <div className="fr-toolbar">
          <InputGroup className="fr-search">
            <InputGroup.Text aria-hidden="true"><i className="fa-solid fa-magnifying-glass"></i></InputGroup.Text>
            <Form.Control
              placeholder="Search..."
              aria-label="Search responses"
              value={search}
              onChange={(e) => { setCurrentPage(1); setSearch(e.target.value); }}
            />
          </InputGroup>
          <Form.Select className="fr-select" aria-label="Filter by form type" value={formTypeFilter} onChange={(e) => { setCurrentPage(1); setFormTypeFilter(e.target.value); }}>
            <option value="">All Form Types</option>
            {formTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Form.Select>
          <Form.Select className="fr-select" aria-label="Filter by project" value={projectFilter} onChange={(e) => { setCurrentPage(1); setProjectFilter(e.target.value); }}>
            <option value="">All Projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Form.Select>
          <Form.Select className="fr-select" aria-label="Filter by status" value={statusFilter} onChange={(e) => { setCurrentPage(1); setStatusFilter(e.target.value); }}>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Form.Select>
          <Form.Select className="fr-select fr-select--wide" aria-label="Filter by review status" value={reviewStatusFilter} onChange={(e) => { setCurrentPage(1); setReviewStatusFilter(e.target.value); }}>
            <option value="">All Review Statuses</option>
            {REVIEW_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </Form.Select>
          <Form.Select className="fr-select" aria-label="Filter by channel" value={channelFilter} onChange={(e) => { setCurrentPage(1); setChannelFilter(e.target.value); }}>
            <option value="">All Channels</option>
            {CHANNEL_OPTIONS.map((c) => <option key={c} value={c}>{c === "PUBLIC_LINK" ? "Public Link" : "Portal"}</option>)}
          </Form.Select>
        </div>

        {isLoading ? (
          <div className="fr-loader"><Spinner animation="border" /><p>Loading responses...</p></div>
        ) : viewRows.length === 0 ? (
          <div className="fr-empty">
            <div className="fr-empty-icon"><i className="fa-solid fa-inbox" aria-hidden="true"></i></div>
            <h5>No responses found</h5>
            <p>Try another filter, or check back after a form is submitted.</p>
          </div>
        ) : (
          <>
            {/* The ONLY horizontal scroller: the table keeps a real min-width
                (every column readable) and scrolls inside this wrapper, never
                the page. Focusable so keyboard users can scroll it. */}
            <div className="fr-table-wrap" tabIndex={0} role="region" aria-label="Form responses table">
              <table className="fr-table">
                <thead>
                  <tr>
                    <th className="fr-col-sub">Submission</th>
                    <th className="fr-col-form">Form</th>
                    <th className="fr-col-project">Project / Module</th>
                    <th className="fr-col-task">Related Task</th>
                    <th className="fr-col-person">Assigned To</th>
                    <th className="fr-col-person">Submitted By</th>
                    <th className="fr-col-badge">Channel</th>
                    <th className="fr-col-badge">Status</th>
                    <th className="fr-col-review">Review</th>
                    <th className="fr-col-score">Score</th>
                    <th className="fr-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {viewRows.map((row) => (
                    <tr key={row.submission_id}>
                      <td className="fr-col-sub">{renderSubmission(row)}</td>
                      <td className="fr-col-form">
                        <strong>{row.form_name}</strong>
                        <div className="text-muted small fr-sub">{row.form_type_name}</div>
                      </td>
                      <td className="fr-col-project">{renderProject(row)}</td>
                      <td className="fr-col-task">{renderTask(row)}</td>
                      <td className="fr-col-person">{renderAssigned(row)}</td>
                      <td className="fr-col-person">{renderSubmittedBy(row)}</td>
                      <td className="fr-col-badge">{renderChannel(row)}</td>
                      <td className="fr-col-badge">{renderStatus(row)}</td>
                      <td className="fr-col-review">{renderReview(row)}</td>
                      <td className="fr-col-score">{renderScore(row)}</td>
                      <td className="fr-col-action">{renderViewButton(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {teamOnly && (
              // Phones: the same rows as stacked cards (CSS swaps which of the
              // two layouts is visible); nothing is dropped.
              <ul className="fr-cards" aria-label="Form responses">
                {viewRows.map((row) => (
                  <li key={row.submission_id} className="fr-card-item">
                    <div className="fr-card-head">
                      <div className="fr-card-title">
                        <strong>{row.form_name}</strong>
                        <div className="text-muted small fr-sub">{row.form_type_name}</div>
                      </div>
                      <span className="fr-card-no">{renderSubmission(row)}</span>
                    </div>
                    <dl className="fr-card-grid">
                      <div><dt>Project / Module</dt><dd>{renderProject(row)}</dd></div>
                      <div><dt>Related Task</dt><dd>{renderTask(row)}</dd></div>
                      <div><dt>Assigned To</dt><dd>{renderAssigned(row)}</dd></div>
                      <div><dt>Submitted By</dt><dd>{renderSubmittedBy(row)}</dd></div>
                      <div><dt>Channel</dt><dd>{renderChannel(row)}</dd></div>
                      <div><dt>Status</dt><dd>{renderStatus(row)}</dd></div>
                      <div><dt>Review</dt><dd>{renderReview(row)}</dd></div>
                      <div><dt>Score</dt><dd>{renderScore(row)}</dd></div>
                    </dl>
                    <div className="fr-card-actions">{renderViewButton(row)}</div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <div className="unit-footer fr-footer">
          <div className="unit-count-text">Showing <strong>{viewRows.length}</strong> of <strong>{viewTotal}</strong> response(s)</div>
          {totalPages > 1 && (
            <Pagination className="unit-pagination">
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} />
              {compactPageItems([...Array(totalPages)].map((_, i) => (
                <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>
              )), currentPage)}
              <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} />
            </Pagination>
          )}
        </div>
      </div>
    </div>
  );
}
